import React, { useState, useEffect } from 'react';
import { AuthUser } from 'aws-amplify/auth';
import { getUserProfile, updateUserProfile, getMyPicks } from '../api';
import { UserProfile, PickResult } from '../types';
import { COUNTRIES, countryCodeToFlag } from '../data/countries';
import { GOLDEN_BOOT_PLAYERS, GOLDEN_BALL_PLAYERS, GOLDEN_GLOVE_PLAYERS } from '../data/wcPlayers';
import { getWcLockState, WcLockState } from '../data/wcLock';
import './Profile.css';

interface Props {
  user: AuthUser;
  displayName: string;
  onDisplayNameChange: (name: string) => void;
}

const BADGE_DESCRIPTIONS: Record<string, { label: string; desc: string }> = {
  '🤖': { label: 'AI Buster', desc: 'Correctly challenged the AI 5+ times' },
  '🧠': { label: 'Purist', desc: '10+ correct predictions in blind mode' },
  '🎯': { label: 'Exacto', desc: '5+ exact scorelines correct' },
  '🔥': { label: 'On Fire', desc: '5 correct predictions in a row' },
  '💀': { label: 'Contrarian', desc: 'Challenged the AI 10+ times total' },
  '⭐': { label: 'Founding Member', desc: 'Signed up in the first 30 days' },
};

function computeStats(picks: PickResult[]) {
  const scored = picks.filter(p => p.status === 'scored');
  const correct = scored.filter(p => p.isCorrect);
  const totalPoints = picks.reduce((s, p) => s + (p.points ?? 0), 0);
  const accuracy = scored.length > 0 ? Math.round((correct.length / scored.length) * 100) : null;
  const blind = picks.filter(p => !p.revealed);

  // Streak: consecutive correct from most recent scored pick
  let streak = 0;
  const sorted = [...scored].sort((a, b) => b.kickoffTime.localeCompare(a.kickoffTime));
  for (const p of sorted) {
    if (p.isCorrect) streak++;
    else break;
  }

  return {
    totalPoints,
    predictions: picks.length,
    scored: scored.length,
    accuracy,
    currentStreak: streak,
    blindRate: picks.length > 0 ? Math.round((blind.length / picks.length) * 100) : null,
  };
}

export default function Profile({ user, displayName, onDisplayNameChange }: Props) {
  const email = user.signInDetails?.loginId ?? '—';
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [picks, setPicks] = useState<PickResult[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [savingName, setSavingName] = useState(false);

  const [supportingCountry, setSupportingCountry] = useState('');
  const [savingCountry, setSavingCountry] = useState(false);
  const [countrySaved, setCountrySaved] = useState(false);

  const [goldenBoot, setGoldenBoot] = useState('');
  const [goldenBall, setGoldenBall] = useState('');
  const [goldenGlove, setGoldenGlove] = useState('');
  const [savingWc, setSavingWc] = useState(false);
  const [wcSaved, setWcSaved] = useState(false);
  const [wcLock, setWcLock] = useState<WcLockState>(() => getWcLockState());

  useEffect(() => {
    Promise.all([getUserProfile(), getMyPicks()]).then(([prof, myPicks]) => {
      setProfile(prof);
      setPicks(myPicks);
      if (prof?.supportingCountry) setSupportingCountry(prof.supportingCountry);
      if (prof?.goldenBoot) setGoldenBoot(prof.goldenBoot);
      if (prof?.goldenBall) setGoldenBall(prof.goldenBall);
      if (prof?.goldenGlove) setGoldenGlove(prof.goldenGlove);
    }).finally(() => setLoading(false));
  }, []);

  async function saveName() {
    const trimmed = draft.trim().slice(0, 20);
    if (!trimmed) return;
    setSavingName(true);
    await updateUserProfile({ displayName: trimmed });
    onDisplayNameChange(trimmed);
    setEditing(false);
    setSavingName(false);
  }

  async function saveCountry(code: string) {
    if (!code) return;
    setSavingCountry(true);
    const country = COUNTRIES.find(c => c.code === code);
    await updateUserProfile({ supportingCountry: code, supportingCountryName: country?.name });
    setSupportingCountry(code);
    setCountrySaved(true);
    setSavingCountry(false);
    setTimeout(() => setCountrySaved(false), 2000);
  }

  useEffect(() => {
    if (wcLock.isLocked) return;
    const id = setInterval(() => {
      const next = getWcLockState();
      setWcLock(next);
      if (next.isLocked) clearInterval(id);
    }, 60_000);
    return () => clearInterval(id);
  }, [wcLock.isLocked]);

  async function saveWcPredictions() {
    setSavingWc(true);
    await updateUserProfile({ goldenBoot, goldenBall, goldenGlove });
    setWcSaved(true);
    setSavingWc(false);
    setTimeout(() => setWcSaved(false), 2000);
  }

  const stats = computeStats(picks);
  const earnedBadges: string[] = [];

  const detectedFlag = profile?.countryFlag ?? null;
  const detectedName = profile?.countryName ?? null;
  const supportingFlag = supportingCountry ? countryCodeToFlag(supportingCountry) : profile?.supportingCountryFlag ?? null;
  const supportingName = COUNTRIES.find(c => c.code === supportingCountry)?.name ?? profile?.supportingCountryName ?? null;

  return (
    <div className="profile-page container">
      <div className="matches-header">
        <h1 className="page-title">Profile</h1>
        <p className="page-sub">Your stats and account</p>
      </div>

      {loading ? (
        <div className="profile-loading">
          <div className="profile-spinner" />
        </div>
      ) : (
        <div className="profile-layout">
          {/* ── Left card ── */}
          <div className="profile-card">
            <div className="profile-avatar">{displayName.slice(0, 2).toUpperCase()}</div>

            {editing ? (
              <div className="dn-edit-wrap">
                <input
                  className="dn-input"
                  value={draft}
                  maxLength={20}
                  autoFocus
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveName();
                    if (e.key === 'Escape') setEditing(false);
                  }}
                />
                <div className="dn-actions">
                  <button className="dn-save" onClick={saveName} disabled={savingName}>
                    {savingName ? 'Saving…' : 'Save'}
                  </button>
                  <button className="dn-cancel" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="dn-view">
                <div className="profile-name">{displayName}</div>
                <button className="dn-edit-btn" onClick={() => { setDraft(displayName); setEditing(true); }}>Edit name</button>
              </div>
            )}

            <div className="profile-email">{email}</div>
            <div className="profile-badge">Free Tier</div>

            {/* Country info */}
            <div className="profile-country-section">
              {detectedFlag && (
                <div className="profile-country-row">
                  <span className="profile-country-flag">{detectedFlag}</span>
                  <span className="profile-country-label">{detectedName}</span>
                  <span className="profile-country-hint">auto-detected</span>
                </div>
              )}

              <div className="profile-country-picker">
                <label className="profile-country-picker-label">Supporting</label>
                <div className="profile-country-picker-wrap">
                  <span className="profile-country-picker-flag">
                    {supportingFlag ?? '🏳️'}
                  </span>
                  <select
                    className="profile-country-select"
                    value={supportingCountry}
                    onChange={e => saveCountry(e.target.value)}
                    disabled={savingCountry}
                  >
                    <option value="">Pick a country…</option>
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                {countrySaved && <span className="profile-country-saved">Saved ✓</span>}
              </div>
            </div>

            {earnedBadges.length > 0 && (
              <div className="profile-badges-wrap">
                {earnedBadges.map(b => (
                  <div key={b} className="profile-badge-item" title={BADGE_DESCRIPTIONS[b]?.desc}>
                    <span className="profile-badge-emoji">{b}</span>
                    <span className="profile-badge-label">{BADGE_DESCRIPTIONS[b]?.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Right stats ── */}
          <div className="profile-stats">
            <h2 className="profile-section-title">Season Stats</h2>
            <div className="profile-stat-grid">
              <div className="profile-stat">
                <div className="ps-val">{stats.totalPoints}</div>
                <div className="ps-label">Total Points</div>
              </div>
              <div className="profile-stat">
                <div className="ps-val">{stats.predictions}</div>
                <div className="ps-label">Predictions</div>
              </div>
              <div className="profile-stat">
                <div className="ps-val">{stats.accuracy !== null ? `${stats.accuracy}%` : '—'}</div>
                <div className="ps-label">Accuracy</div>
              </div>
              <div className="profile-stat">
                <div className="ps-val">{stats.currentStreak > 0 ? `🔥 ${stats.currentStreak}` : '—'}</div>
                <div className="ps-label">Streak</div>
              </div>
            </div>

            <div className="profile-stat-grid" style={{ marginTop: 12 }}>
              <div className="profile-stat profile-stat-highlight">
                <div className="ps-val ps-val-blind">{stats.blindRate !== null ? `${stats.blindRate}%` : '—'}</div>
                <div className="ps-label">Blind Rate</div>
                <div className="ps-hint">% picked without AI</div>
              </div>
              <div className="profile-stat profile-stat-highlight">
                <div className="ps-val ps-val-bust">{stats.scored > 0 ? `${stats.scored}` : '—'}</div>
                <div className="ps-label">Scored</div>
                <div className="ps-hint">finished matches</div>
              </div>
            </div>

            {/* Badges */}
            <div className="profile-badges-section">
              <h2 className="profile-section-title" style={{ marginTop: '28px', marginBottom: '12px' }}>Badges</h2>
              {earnedBadges.length > 0 ? (
                <div className="profile-all-badges">
                  {earnedBadges.map(b => (
                    <div key={b} className="profile-badge-card">
                      <span className="pbc-emoji">{b}</span>
                      <div>
                        <div className="pbc-label">{BADGE_DESCRIPTIONS[b]?.label}</div>
                        <div className="pbc-desc">{BADGE_DESCRIPTIONS[b]?.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="profile-badges-empty">
                  <p>No badges yet — make predictions to earn them.</p>
                  <div className="profile-badges-preview">
                    {Object.entries(BADGE_DESCRIPTIONS).map(([emoji, info]) => (
                      <div key={emoji} className="profile-badge-card profile-badge-card--locked">
                        <span className="pbc-emoji pbc-emoji--locked">{emoji}</span>
                        <div>
                          <div className="pbc-label">{info.label}</div>
                          <div className="pbc-desc">{info.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* World Cup 2026 Predictions */}
            <div className="wc-pred-header">
              <h2 className="profile-section-title" style={{ margin: 0 }}>🏆 World Cup 2026 Predictions</h2>
              {wcLock.isLocked ? (
                <span className="wc-pred-locked-badge">🔒 Locked</span>
              ) : (
                <span className={`wc-pred-countdown${wcLock.urgency === 'soon' ? ' wc-pred-countdown--urgent' : ''}`}>
                  ⏰ {wcLock.label}
                </span>
              )}
            </div>

            {wcLock.isLocked && (
              <div className="wc-pred-lock-notice">
                The tournament has started. Your predictions are locked and cannot be changed.
              </div>
            )}

            <div className="wc-predictions-grid" style={{ marginTop: '12px' }}>
              {[
                { label: '🥾 Golden Boot — Top Scorer', value: goldenBoot, setter: setGoldenBoot, players: GOLDEN_BOOT_PLAYERS },
                { label: '⚽ Golden Ball — Best Player', value: goldenBall, setter: setGoldenBall, players: GOLDEN_BALL_PLAYERS },
                { label: '🧤 Golden Glove — Best Goalkeeper', value: goldenGlove, setter: setGoldenGlove, players: GOLDEN_GLOVE_PLAYERS },
              ].map(({ label, value, setter, players }) => (
                <div key={label} className="wc-pred-field">
                  <label className="wc-pred-label">{label}</label>
                  {wcLock.isLocked ? (
                    <div className="wc-pred-locked-value">
                      {value || <span className="wc-pred-no-pick">No prediction made</span>}
                    </div>
                  ) : (
                    <select
                      className="wc-pred-select"
                      value={value}
                      onChange={e => setter(e.target.value)}
                      disabled={savingWc}
                    >
                      <option value="">Select a player…</option>
                      {players.map(p => (
                        <option key={p.name} value={p.name}>{p.name} · {p.country}</option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
            </div>

            {!wcLock.isLocked && (
              <button className="wc-pred-save" onClick={saveWcPredictions} disabled={savingWc}>
                {savingWc ? 'Saving…' : wcSaved ? '✓ Saved' : 'Save Predictions'}
              </button>
            )}

            {/* Account details */}
            <h2 className="profile-section-title" style={{ marginTop: '28px' }}>Account Details</h2>
            <div className="account-table">
              <div className="account-row">
                <span className="account-key">Display Name</span>
                <span className="account-val">{displayName}</span>
              </div>
              <div className="account-row">
                <span className="account-key">Email</span>
                <span className="account-val">{email}</span>
              </div>
              {detectedName && (
                <div className="account-row">
                  <span className="account-key">Location</span>
                  <span className="account-val">{detectedFlag} {detectedName}</span>
                </div>
              )}
              {supportingName && (
                <div className="account-row">
                  <span className="account-key">Supporting</span>
                  <span className="account-val">{supportingFlag} {supportingName}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
